import useGetMyRegionPreferences from '@/api/queries/me/useGetMyRegionPreferences';
import { formatPreferredCurrency } from '@/hooks/usePreferredCurrencyFormat';
import {
  Button,
  ButtonRadioGroup,
  Card,
  Checkbox,
  CurrencyInput,
  Field,
  H4,
  Input,
  InputGroup,
  isRichTextContentEmpty,
  NumberInput,
  RichTextarea,
  Text,
  Tooltip,
} from '@payaca/ambrosia';
import { percentageFromAmount } from '@payaca/helpers/paymentScheduleHelper';
import UntitledIcon from '@payaca/untitled-icons';
import { FC, PropsWithChildren, ReactNode, useState } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import {
  roundPercentageForDisplay,
  withDepositBasisType,
} from './stageHelpers';
import { PaymentScheduleFormValues } from './types';

type PercentageControl = {
  name: string;
  value: number | null;
  onChange: (value: number | null) => void;
  onBlur: () => void;
};

const CardShell: FC<
  PropsWithChildren<{
    index: number;
    /** Controls shown before the remove button, e.g. the deposit checkbox. */
    actions?: ReactNode;
    /** Shows the card as invalid even when its own fields are valid. */
    isInvalid?: boolean;
    onRemove: () => void;
  }>
> = (props) => {
  const { index, actions, isInvalid, onRemove, children } = props;

  const {
    formState: { errors },
  } = useFormContext<PaymentScheduleFormValues>();

  return (
    <Card
      sizeVariant="sm"
      variant={
        isInvalid || errors.paymentSchedule?.stages?.[index]
          ? 'error'
          : 'default'
      }
      role="group"
      aria-label={`Stage ${index + 1}`}
    >
      <Card.Body className="flex flex-col gap-4">
        <div className="flex items-center justify-between gap-2">
          <H4>Stage {index + 1}</H4>
          <div className="flex items-center gap-1">
            {actions}
            <Button
              variant="quaternary"
              aria-label="Remove stage"
              onClick={onRemove}
            >
              <UntitledIcon name="trash-01.3" className="w-4 h-4" />
            </Button>
          </div>
        </div>
        {children}
      </Card.Body>
    </Card>
  );
};

const DescriptionField: FC<{ index: number }> = (props) => {
  const { index } = props;
  const { control } = useFormContext<PaymentScheduleFormValues>();

  return (
    <Controller
      name={`paymentSchedule.stages.${index}.description`}
      control={control}
      render={({ field: { name, ...attributes } }) => (
        <Field name={name}>
          <Field.Label>Stage description</Field.Label>
          <Input sizeVariant="sm" {...attributes} />
        </Field>
      )}
    />
  );
};

const PercentageField: FC<{
  percentage: PercentageControl;
  label?: string;
  /** Shown beside the label, e.g. a link that clears the field. */
  labelAction?: ReactNode;
  autoFocus?: boolean;
}> = (props) => {
  const { percentage, label = 'Percentage', labelAction, autoFocus } = props;

  return (
    <div className="w-40">
      <Field name={percentage.name}>
        <div className="flex items-baseline justify-between gap-2">
          <Field.Label>{label}</Field.Label>
          {labelAction}
        </div>
        <InputGroup sizeVariant="sm">
          <NumberInput
            as={InputGroup.RawInput}
            autoFocus={autoFocus}
            value={percentage.value}
            onChange={(value) => percentage.onChange(value)}
            onBlur={percentage.onBlur}
            format={{ maximumFractionDigits: 2 }}
          />
          <InputGroup.Icon name="percent-02.3" />
        </InputGroup>
      </Field>
    </div>
  );
};

const AmountField: FC<{
  name: string;
  /** In minor units, inc tax. */
  amount: number | null;
  /** Omit to disable the input, e.g. when the Proposal has no total yet. */
  onAmountChange?: (amount: number) => void;
  onBlur?: () => void;
  /** Explains the amount in a tooltip beside the label. */
  hint?: { label: string; content: ReactNode };
}> = (props) => {
  const { name, amount, onAmountChange, onBlur, hint } = props;
  const { data: regionPreferences } = useGetMyRegionPreferences();

  return (
    <div className="w-40">
      <Field name={name}>
        <div className="flex items-center gap-1">
          <Field.Label>Amount</Field.Label>
          {hint && (
            <Tooltip
              showArrow
              tooltipContent={<div className="max-w-60">{hint.content}</div>}
            >
              <Button variant="quaternary" size="xs" aria-label={hint.label}>
                <UntitledIcon name="info-circle.3" className="h-4 w-4" />
              </Button>
            </Tooltip>
          )}
        </div>
        <CurrencyInput
          sizeVariant="sm"
          value={amount ?? undefined}
          disabled={!onAmountChange}
          onChange={(value) => onAmountChange?.(value)}
          onBlur={onBlur}
          currency={regionPreferences?.currency}
        />
      </Field>
    </div>
  );
};

/** A stage's percentage, and its amount, which sets the percentage. */
const PercentageAndAmountFields: FC<{
  index: number;
  /** `onExactChange` sets the percentage an amount comes to, unsnapped. */
  percentage: PercentageControl & {
    onExactChange: (value: number | null) => void;
  };
  /** Proposal total in minor units, inc tax. */
  proposalTotal: number;
  /** The stage's share of the total in minor units, inc tax. */
  amount: number;
}> = (props) => {
  const { index, percentage, proposalTotal, amount } = props;

  return (
    <div className="flex flex-wrap gap-4">
      <PercentageField percentage={percentage} />
      <AmountField
        name={`paymentSchedule.stages.${index}.amount`}
        amount={amount}
        onAmountChange={
          proposalTotal > 0
            ? (value) =>
                percentage.onExactChange(
                  percentageFromAmount(value, proposalTotal)
                )
            : undefined
        }
      />
    </div>
  );
};

const DepositCoverNote: FC<{
  /** The stage's share of the total in minor units, inc tax. */
  amount: number;
  /** In minor units. */
  amountCoveredByDeposit: number;
}> = (props) => {
  const { amount, amountCoveredByDeposit } = props;
  const { data: regionPreferences } = useGetMyRegionPreferences();
  const currency = regionPreferences?.currency;
  if (!currency) return null;

  const format = (value: number) =>
    formatPreferredCurrency({ value, currency });
  const amountToInvoice = amount - amountCoveredByDeposit;

  return (
    <Text level="supporting">
      {amountToInvoice > 0
        ? `${format(amountCoveredByDeposit)} covered by the deposit · ${format(amountToInvoice)} to invoice`
        : 'Fully covered by the deposit'}
    </Text>
  );
};

/** Moves a stage's percentage by whatever is left to bring the total to 100%. */
const AdjustPercentageButton: FC<{
  percentage: PercentageControl;
  remainingPercentage: number;
}> = (props) => {
  const { percentage, remainingPercentage } = props;

  return (
    <Tooltip
      tooltipContent={
        <div className="max-w-52">
          Adjust the percentage of this stage{' '}
          {remainingPercentage < 0 ? 'down' : 'up'} to bring the total closer to
          100%
        </div>
      }
    >
      <Button
        variant="quaternary"
        aria-label={
          remainingPercentage < 0
            ? 'Decrease stage percentage to reach 100%'
            : 'Increase stage percentage to reach 100%'
        }
        disabled={remainingPercentage === 0}
        onClick={() =>
          percentage.onChange(
            Math.max((percentage.value ?? 0) + remainingPercentage, 0)
          )
        }
      >
        <UntitledIcon
          name={remainingPercentage < 0 ? 'arrow-down' : 'arrow-up'}
          className="w-4 h-4"
        />
      </Button>
    </Tooltip>
  );
};

const DepositCheckbox: FC<{
  index: number;
  isDeposit: boolean;
  onChange: (isDeposit: boolean) => void;
}> = (props) => {
  const { index, isDeposit, onChange } = props;

  return (
    <Field name={`paymentSchedule.stages.${index}.type`}>
      <Checkbox
        label="Deposit"
        checked={isDeposit}
        onChange={() => onChange(!isDeposit)}
      />
    </Field>
  );
};

const DepositTypeField: FC<{
  index: number;
  /** In minor units. Switching keeps what the deposit comes to on it. */
  total?: number;
}> = (props) => {
  const { index, total } = props;
  const { control } = useFormContext<PaymentScheduleFormValues>();

  return (
    <Controller
      name={`paymentSchedule.stages.${index}.basis`}
      control={control}
      render={({ field }) => (
        <Field name={`${field.name}.type`}>
          <Field.Label>Deposit type</Field.Label>
          <div className="flex">
            <ButtonRadioGroup<'percentage' | 'fixed'>
              aria-label="Deposit type"
              sizeVariant="xs"
              options={[
                { label: 'Percentage', value: 'percentage' },
                { label: 'Fixed amount', value: 'fixed' },
              ]}
              value={field.value.type}
              onChange={(type) =>
                field.onChange(
                  withDepositBasisType(field.value, type, total ?? 0)
                )
              }
            />
          </div>
        </Field>
      )}
    />
  );
};

/** A fixed deposit's amount and its optional cap as a percentage of the total. */
const FixedDepositFields: FC<{ index: number }> = (props) => {
  const { index } = props;
  const { control, setValue } = useFormContext<PaymentScheduleFormValues>();

  const capPath =
    `paymentSchedule.stages.${index}.basis.percentageCap` as const;
  const percentageCap = useWatch({ control, name: capPath });
  const [isAddingCap, setIsAddingCap] = useState(false);
  const isCapShown = isAddingCap || percentageCap != null;

  const clearCap = () => {
    setIsAddingCap(false);
    setValue(capPath, null, { shouldDirty: true, shouldValidate: true });
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap gap-4">
        <Controller
          name={`paymentSchedule.stages.${index}.basis.fixedAmount`}
          control={control}
          render={({ field }) => (
            <AmountField
              name={field.name}
              amount={field.value}
              onAmountChange={field.onChange}
              onBlur={field.onBlur}
              hint={{
                label: 'How a fixed deposit comes out of the stages',
                content:
                  'Comes out of any unallocated balance first, then each stage in order, so it may reduce stage values',
              }}
            />
          )}
        />
        {isCapShown && (
          <Controller
            name={capPath}
            control={control}
            render={({ field }) => (
              <PercentageField
                percentage={field}
                label="Cap"
                autoFocus={isAddingCap}
                labelAction={
                  <Button variant="link" size="sm" onClick={clearCap}>
                    Clear cap
                  </Button>
                }
              />
            )}
          />
        )}
      </div>
      {percentageCap != null ? (
        <Text level="supporting">
          Never more than {percentageCap}% of the total
        </Text>
      ) : (
        !isCapShown && (
          <div>
            <Button
              variant="link"
              size="sm"
              onClick={() => setIsAddingCap(true)}
            >
              Set cap
            </Button>
          </div>
        )
      )}
    </div>
  );
};

const Header: FC<{ allocatedPercentage: number }> = (props) => {
  const { allocatedPercentage } = props;

  return (
    <div className="flex items-center justify-between gap-2">
      <H4>Payment schedule</H4>
      <H4>{roundPercentageForDisplay(allocatedPercentage)}% allocated</H4>
    </div>
  );
};

const AddScheduleButton: FC<{ onClick: () => void }> = (props) => {
  const { onClick } = props;

  return (
    <Button
      variant="tertiary"
      onClick={onClick}
      className="focus:ring-0 w-full"
    >
      <UntitledIcon name="plus.3" className="w-4 h-4" />
      Add payment schedule
    </Button>
  );
};

const AddStageButton: FC<{
  remainingPercentage: number;
  onClick: () => void;
}> = (props) => {
  const { remainingPercentage, onClick } = props;

  return (
    <div>
      <Button
        variant="tertiary"
        onClick={onClick}
        className="focus:ring-0"
        size="sm"
      >
        <UntitledIcon name="plus.3" className="w-4 h-4" />
        Add stage ({roundPercentageForDisplay(Math.max(remainingPercentage, 0))}
        % remaining)
      </Button>
    </div>
  );
};

const ExplainerField: FC = () => {
  const { control } = useFormContext<PaymentScheduleFormValues>();

  return (
    <Controller
      name="paymentSchedule.explainer"
      control={control}
      render={({ field: { name, value, onChange } }) => (
        <Field name={name}>
          <Field.Label>Payment schedule explainer text</Field.Label>
          <RichTextarea
            aria-label="Payment schedule explainer text"
            value={value || ''}
            onChange={(newValue) =>
              onChange(isRichTextContentEmpty(newValue) ? null : newValue)
            }
          />
        </Field>
      )}
    />
  );
};

export const PaymentScheduleStage = {
  Header,
  AddScheduleButton,
  AddStageButton,
  ExplainerField,
  CardShell,
  DescriptionField,
  PercentageField,
  AmountField,
  PercentageAndAmountFields,
  DepositCoverNote,
  AdjustPercentageButton,
  DepositCheckbox,
  DepositTypeField,
  FixedDepositFields,
};
